import { FC, useEffect, useState } from 'react';
import { Icon } from 'common-components';
import classNames from 'classnames';
import styles from './select.module.scss';
import { CategoryProps, SelectProps } from './selectTypes';
import Tag from '../../tag/tag';
import TagsFilter from '../tagsFilter/tagsFilter';
import { TagFilterProps } from '../tagsFilter/tagsFilterTypes';
import { useTranslationContext } from '../../contexts/translationContext';
import { sortArrayOfObjects } from '../../utils';

const Select: FC<SelectProps> = (props: SelectProps) => {
    const {
        tags,
        onTagClickCallback,
        width = '12rem',
        selectedTagsCallback = [],
        maxRows = 4,
        categories,
        maxHeight,
        isMultiSelect = true
    } = props;
    const [selectedTags, setSelectedTags] = useState<(string | object)[]>([]);
    const [isTagsFilterOpen, setIsTagsFilterOpen] = useState<boolean>(false);
    const [tagsFilterItems, setTagsFilterItems] = useState<TagFilterProps[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<CategoryProps>();

    const ROW_HEIGHT_REM = 2.2;
    const DEFAULT_TOP_POPULAR = 5;
    const { translate } = useTranslationContext();
    const SHOW_MORE = translate('ShowMore');

    useEffect(() => {
        setSelectedTags(selectedTagsCallback);
    }, [selectedTagsCallback.length]);

    const onTagClick = (tagKey, isChecked) => {
        let selectedTagsCpy = [...selectedTags];
        if (isMultiSelect) {
            if (isChecked) {
                selectedTagsCpy = [...selectedTagsCpy, tagKey];
            } else {
                selectedTagsCpy.splice(selectedTagsCpy.indexOf(tagKey), 1);
            }
        } else {
            selectedTagsCpy = [tagKey];
        }
        setSelectedTags(selectedTagsCpy);
        onTagClickCallback?.(selectedTagsCpy);
    };

    const onTagsFilterOpen = category => {
        setSelectedCategory(category);
        let categoryTags = tags?.filter(tag => tag.categoryKey == category.key);
        categoryTags = sortArrayOfObjects(categoryTags || [], 'priority', true);
        const tagFilterTags: any = categoryTags?.map(tag => {
            return { name: tag.tagKey, displayName: tag.title };
        });
        setTagsFilterItems(tagFilterTags);
        setIsTagsFilterOpen(true);
    };

    const onTagsFilterApply = tagsFilterSelectedTags => {
        setSelectedTags([...selectedTags, ...tagsFilterSelectedTags]);
        onTagClickCallback?.([...selectedTags, ...tagsFilterSelectedTags]);
        setIsTagsFilterOpen(false);
    };

    const selectedTagsCategoryCount = categoryKey => {
        const count = tags?.filter(tag => {
            return selectedTags.includes(tag.tagKey) && tag.categoryKey == categoryKey;
        }).length;
        return count ? `(${count})` : '';
    };

    return (
        <div
            data-testid="select"
            className={classNames(styles['select-container'])}
            style={{ width, maxHeight: maxHeight || `${maxRows * ROW_HEIGHT_REM}rem` }}
        >
            {isTagsFilterOpen && (
                <div className={styles['tags-filter-container']}>
                    <TagsFilter
                        tags={tagsFilterItems}
                        selected={selectedTags}
                        title={selectedCategory?.displayName || ''}
                        searchTitle={''}
                        numberOfPapular={selectedCategory?.topPopular || DEFAULT_TOP_POPULAR}
                        onApply={onTagsFilterApply}
                        onClose={() => setIsTagsFilterOpen(false)}
                    />
                </div>
            )}
            {categories ? (
                categories.map((category, index) => {
                    let categoryTags = tags?.filter(tag => tag.categoryKey == category.key);
                    categoryTags = sortArrayOfObjects(categoryTags || [], 'priority', true);
                    const hiddenTagsFlag = category.topPopular && categoryTags.length > category.topPopular;
                    if (category.topPopular) {
                        categoryTags = categoryTags.slice(0, category.topPopular);
                    }
                    return (
                        <div key={index}>
                            <div className={styles['category-title']}>
                                <span className={styles.title}>
                                    {category.displayName}
                                    <span className={styles.info}>{`${selectedTagsCategoryCount(category.key)}`}</span>
                                </span>
                            </div>
                            <div
                                className={styles['tags-container']}
                                style={{ maxHeight: `${category.maxHeightRem || 10}rem` }}
                            >
                                {categoryTags.map((tag, index) => {
                                    return (
                                        <Tag
                                            key={index}
                                            tagKey={tag?.tagKey}
                                            title={tag?.title}
                                            onTagClick={(tagKey, isChecked) => onTagClick(tagKey, isChecked)}
                                            isCheckedCallback={selectedTags.includes(tag?.tagKey)}
                                        />
                                    );
                                })}
                            </div>
                            {hiddenTagsFlag && (
                                <div className={styles['category-footer']} onClick={() => onTagsFilterOpen(category)}>
                                    <span>{SHOW_MORE}</span>
                                    <Icon
                                        name={'downArrowIcon'}
                                        className={styles.icon}
                                        style={{ transform: 'rotate(180deg' }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })
            ) : (
                <div className={styles['tags-container']}>
                    {tags?.map((tag, index) => {
                        return (
                            <Tag
                                key={index}
                                tagKey={tag?.tagKey}
                                title={tag?.title}
                                onTagClick={onTagClick}
                                isCheckedCallback={selectedTags.includes(tag?.tagKey)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Select;
